import React, { Dispatch, SetStateAction, useEffect } from "react";
import { useSelector } from "react-redux";
import { selectOrganization } from "go-security/services/organizations/selectors";
import {
	DeliveryEstimationApi,
	LiveOrderApi,
} from "../../../../../../../../../../../../services/Api/Organization/types";
import { isWoltDriveAssigned } from "../../../../../../services/orderDelivery";
import { LiveOrderDeliveryOptionType } from "../../../../../../services/types";
import { checkIfDeliveryOptionIsDisabled, isWoltDriveEnabled } from "../../../../../../services/utils";
import { useEstimateDelivery } from "../../hooks/useEstimateDelivery";
import LiveOrderDeliveryOption from "../LiveOrderDeliveryOptions/LiveOrderDeliveryOption";

interface Props {
	setSelectedDeliveryOption: (option: LiveOrderDeliveryOptionType | undefined) => void;
	selectedDeliveryOption: LiveOrderDeliveryOptionType | undefined;
	selectedTimeOption: number | undefined | string;
	setEstimatedDeliveryData: (data: DeliveryEstimationApi | undefined) => void;
	estimatedDeliveryData: DeliveryEstimationApi | undefined;
	handleToggle: (option?: LiveOrderDeliveryOptionType) => void;
	setSelectedTimeOption?: (option: number | undefined | string) => void;
	order: LiveOrderApi;
	orderEstimatedPreparationInMinutes?: number;
	orderDate: string | undefined;
	firstEstimatedDeliveryData: DeliveryEstimationApi | undefined;
	setFirstEstimatedDeliveryData: React.Dispatch<React.SetStateAction<DeliveryEstimationApi | undefined>>;
	estimatingLoading: boolean;
	setEstimatingLoading: Dispatch<SetStateAction<boolean>>;
}

const LiveOrderAcceptanceModalDeliveryOptions = ({
	setSelectedDeliveryOption,
	selectedDeliveryOption,
	selectedTimeOption,
	setEstimatedDeliveryData,
	estimatedDeliveryData,
	handleToggle,
	order,
	orderEstimatedPreparationInMinutes,
	orderDate,
	firstEstimatedDeliveryData,
	setFirstEstimatedDeliveryData,
	setEstimatingLoading,
	estimatingLoading,
}: Props) => {
	const organization = useSelector(selectOrganization);
	const woltDriveEnabled = isWoltDriveEnabled(organization);
	const isWoltDrive = isWoltDriveAssigned(order);
	const { handleEstimateDelivery, isOutOfDeliveryZone } = useEstimateDelivery({
		orderId: order.id,
		setEstimatedDeliveryData,
		setEstimatingLoading,
	});

	const getDeliveryOptions = () => {
		const options = [LiveOrderDeliveryOptionType.OWN_DRIVER];
		if (woltDriveEnabled) options.push(LiveOrderDeliveryOptionType.WOLT_DRIVE);

		return options;
	};

	const handleInit = async (data?: string) => {
		const res = await handleEstimateDelivery(data);
		setFirstEstimatedDeliveryData(res);
	};

	useEffect(() => {
		if (woltDriveEnabled && !isWoltDrive) handleInit(order.pickup_at ? orderDate : undefined);
	}, []);

	useEffect(() => {
		if (selectedDeliveryOption === LiveOrderDeliveryOptionType.WOLT_DRIVE && selectedTimeOption) {
			handleEstimateDelivery(selectedTimeOption);
		}
	}, [selectedDeliveryOption, handleEstimateDelivery, selectedTimeOption]);

	if (!woltDriveEnabled) return <></>;

	return (
		<>
			<div className="d-flex flex-wrap gap-3 live-order-delivery-options">
				{getDeliveryOptions().map((deliveryOption) => (
					<LiveOrderDeliveryOption
						option={deliveryOption}
						key={deliveryOption}
						setSelectedDeliveryOption={setSelectedDeliveryOption}
						selectedDeliveryOption={selectedDeliveryOption}
						estimatingLoading={
							deliveryOption === LiveOrderDeliveryOptionType.WOLT_DRIVE ? estimatingLoading : undefined
						}
						estimatedDeliveryData={
							deliveryOption === LiveOrderDeliveryOptionType.WOLT_DRIVE
								? estimatedDeliveryData
								: undefined
						}
						firstEstimatedDeliveryData={firstEstimatedDeliveryData}
						disabled={checkIfDeliveryOptionIsDisabled(
							deliveryOption,
							isWoltDrive,
							isOutOfDeliveryZone,
							order
						)}
						isOutOfDeliveryZone={isOutOfDeliveryZone}
						handleToggle={handleToggle}
						orderEstimatedPreparationInMinutes={orderEstimatedPreparationInMinutes}
						deliveryMoney={order?.delivery_cost}
						selectedTimeOption={selectedTimeOption}
					/>
				))}
			</div>
			{selectedDeliveryOption && <hr />}
		</>
	);
};

export default LiveOrderAcceptanceModalDeliveryOptions;
